import React from 'react';

import styled from '@emotion/styled';
import { Divider, Space, Menu, Button, DatePicker } from 'antd';
import { SpaceSize } from 'antd/lib/space';
import Input from '../Input';
import classname from 'classnames';
import { Option } from 'app/types/interfaces/Option';
import Dropdown from '../Dropdown';
import Icon, { CheckOutlined, DownOutlined } from '@ant-design/icons';
import { MenuInfo } from 'rc-menu/lib/interface';
import { ReactComponent as SortIcon } from 'assets/icons/toolbars/sort-icon.svg';
import first from 'lodash/first';
import MoreMenu from './components/MoreMenu';
import classes from './Toolbar.module.scss';
import { Moment } from 'moment';

const StyledDropdownContent = styled.div`
  padding: 8px;
  background: white;
  box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%),
    0 9px 28px 8px rgb(0 0 0 / 5%);
  li[role='menuitem'] {
    padding-left: 4px;
    background: transparent;
    &:hover {
      background: #ebf1ff;
    }
  }
  .ant-dropdown-menu {
    box-shadow: none;
  }
  .ant-divider {
    margin: 8px 0;
  }
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 1px 6px 0px #a5b2c833;
  box-shadow: 0px 2px 4px 1px #a5b2c81a;
`;

const StyledSearchInput = styled(Input.Search)`
  min-width: 30vw;
  .ant-input-group-addon {
    display: none;
  }
`;

type SorterProps = {
  options: Array<Option>;
  directions: Array<Option>;
  direction?: 'desc' | 'asc' | undefined;
  value?: string[] | undefined;
  onApply: (
    value: string,
    direction: string,
    dateRange?: [Moment | undefined, Moment | undefined]
  ) => void;
  range?: boolean;
  dateRange?: [Moment | undefined, Moment | undefined];
};

const Toolbar = ({
  children,
  className
}: {
  className?: any;
  children: React.ReactNode;
  size?: SpaceSize;
}) => {
  return (
    <Container className={classname(classes.filterBar, 'toolbar', className)}>
      <div className={classes.left}>
        {React.Children.toArray(children).map((child) => {
          return child;
        })}
      </div>
    </Container>
  );
};

const Sorter = (props: SorterProps) => {
  const { range = true } = props;
  const [open, setOpen] = React.useState(false);
  const [selectedValue, setValue] = React.useState(props.value);
  const [direction, setDirection] = React.useState(props.direction || 'desc');
  const [rangeValue, setRangeValue] = React.useState(props.dateRange);

  const menuItems = React.useMemo(() => {
    const mapped = props.options.map(({ value, label }) => {
      return {
        key: value,
        label: (
          <Space>
            <CheckOutlined
              style={{
                visibility:
                  (selectedValue || [])[0] !== value ? 'hidden' : 'unset'
              }}
            />
            {label}
          </Space>
        )
      };
    });

    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options, selectedValue]);

  const directionItems = React.useMemo(() => {
    const mapped = props.directions.map(({ value, label }) => {
      return {
        key: value,
        label: (
          <Space>
            <CheckOutlined
              style={{ visibility: direction !== value ? 'hidden' : 'unset' }}
            />
            {label}
          </Space>
        )
      };
    });

    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.directions, direction]);

  // const label = React.useMemo(() => {
  //   const selectedLabels = props.options
  //     .filter((option) => props.value?.includes(option.value))
  //     .map(({ label }) => label);
  //   return selectedLabels.join(',');
  // }, [props.value, props.options]);

  const onClick = (info: MenuInfo) => {
    setValue([info.key]);
  };
  const onClickDirection = (info: MenuInfo) => {
    setDirection(info.key as any);
  };

  const onChangeDateRange = (values) => {
    setRangeValue(values);
  };

  const onApply = () => {
    props.onApply(first(selectedValue) || '', direction, rangeValue);
    setTimeout(() => {
      setOpen(false);
    });
  };

  const onReset = () => {
    setValue(props.value);
    setDirection(props.direction || 'desc');
    setRangeValue(props.dateRange);
  };

  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  React.useEffect(() => {
    setDirection(props.direction || 'desc');
  }, [props.direction]);

  React.useEffect(() => {
    setRangeValue(props.dateRange);
  }, [props.dateRange]);

  React.useEffect(() => {
    if (!open) {
      setValue(props.value);
      setDirection(props.direction || 'desc');
      setRangeValue(props.dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      menu={{ items: menuItems, selectedKeys: selectedValue, onClick }}
      showSearch={false}
      dropdownRender={(menus) => {
        return (
          <StyledDropdownContent>
            {menus}
            <Divider></Divider>
            <Menu
              onClick={onClickDirection}
              selectedKeys={[direction]}
              items={directionItems}
            ></Menu>
            {range && (
              <DatePicker.RangePicker
                value={rangeValue}
                onChange={onChangeDateRange}
              />
            )}
            <Dropdown.Footer
              actions={[
                {
                  key: 'apply',
                  label: 'Apply',
                  buttonProps: {
                    type: 'primary',
                    onClick: onApply
                  }
                },
                {
                  key: 'reset',
                  label: 'Reset',
                  buttonProps: {
                    onClick: onReset
                  }
                }
              ]}
            ></Dropdown.Footer>
          </StyledDropdownContent>
        );
      }}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <Button
        type="text"
        icon={
          <Icon
            className={classes.sortIcon}
            style={{
              fontSize: 18,
              verticalAlign: 'middle',
              marginRight: 8,
              fill: '#1890ff'
            }}
            component={SortIcon}
          ></Icon>
        }
      >
        <a
          type="link"
          onClick={(e) => {
            setOpen(true);
            e.preventDefault();
          }}
          style={{ color: '#3A4861' }}
        >
          <Space size={8}>
            <span>Sort by Date</span>
            <DownOutlined />
          </Space>
        </a>
      </Button>
    </Dropdown>
  );
};
Toolbar.SearchInput = StyledSearchInput;
Toolbar.Sorter = Sorter;
Toolbar.MoreMenu = MoreMenu;
export default Toolbar;
